import { ExternalLink, Stethoscope } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { DiscriminatedProviderStatusChangedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface ProviderStatusChangedEventProps {
  event: DiscriminatedProviderStatusChangedEvent;
  matterId: string;
}

export const ProviderStatusChangedEvent: React.FC<
  ProviderStatusChangedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent } = event;
  const { provider, old_status, new_status } = payload;

  // Get provider name
  const providerName = provider.name || 'Unknown Provider';

  // Get provider type
  const providerType = provider.provider_type
    ? formatProviderType(provider.provider_type)
    : null;

  // Format statuses
  const oldStatusLabel = old_status ? formatStatus(old_status) : 'None';
  const newStatusLabel = new_status ? formatStatus(new_status) : 'None';

  // Determine badge variant based on new status
  const newStatusVariant = new_status
    ? getStatusVariant(new_status)
    : 'outline';

  return (
    <EventContainer>
      <EventHeader
        icon={<Stethoscope size={20} />}
        title="Provider Status Changed"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Provider Name and Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{providerName}</span>
            {providerType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{providerType}</Badge>
              </>
            )}
          </div>

          {/* Address */}
          {provider.address && (
            <div className="text-xs">
              <span className="text-slate-600">Address:</span>{' '}
              <span className="text-[11px]">{provider.address}</span>
            </div>
          )}

          {/* Status Change */}
          <div className="flex items-center gap-2 text-xs mt-2">
            <span className="text-slate-600">Status:</span>
            <Badge variant="outline" className="line-through">
              {oldStatusLabel}
            </Badge>
            <span className="text-slate-400">→</span>
            <Badge variant={newStatusVariant}>{newStatusLabel}</Badge>
          </div>
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/treatment/${provider.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Provider
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatProviderType(type: string): string {
  const types: Record<string, string> = {
    ORGANIZATION: 'Organization',
    PRACTITIONER: 'Practitioner',
  };
  return types[type] || type;
}

function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    NEEDS_APPROVAL: 'Needs Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'APPROVED':
      return 'default'; // Green
    case 'REJECTED':
      return 'destructive'; // Red
    case 'NEEDS_APPROVAL':
      return 'secondary'; // Blue
    default:
      return 'outline';
  }
}
